/**
 * A Inbox comercial (D02, §6/§11.2) — a coluna esquerda da tela de
 * atendimento.
 *
 * ── O que a diferencia da Inbox do Chatwoot ────────────────────────────────
 *
 * `resumo_comercial` em CADA item. A D02 é categórica: *"o contexto
 * comercial resumido vem de `conversation_links` mais `read.opportunities`.
 * É o que diferencia esta Inbox da do Chatwoot — e por isso não pode ser
 * opcional."* Um item sem oportunidade traz `vinculo.estado` explicando o
 * porquê; o que não existe é item mudo (a armadilha nomeada na H04).
 *
 * ── Campos do inventário A07 que ainda não têm fonte ───────────────────────
 *
 * Mesma disciplina da C24: campo sem fonte NÃO entra fingindo existir.
 * Ficam de fora, com dono: etiquetas, time e `espera_desde` (não estão em
 * `read.conversations` — a projeção da C19 copia "só o que a tela usa", e
 * ampliá-la é migração nova); SLA (`estado`/`prazo_em`) é a D08, que ainda
 * não tem regra definida — e a A07 é explícita: *"sem regra definida, não
 * inventar indicador"*; avatar e `adiada_ate` seguem o mesmo caminho.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';
import { SituacaoDaConversaSchema } from './conversas.js';
import { InstanteSchema } from './valores.js';

export const ItemDaInboxSchema = z.object({
  conversa: z.object({
    id: IdSchema,
    /** ID na engine — campo de REFERÊNCIA (§3.2), para o rodapé de suporte. */
    referencia_externa: z.string(),
    situacao: SituacaoDaConversaSchema,
    prioridade: z.enum(['baixa', 'media', 'alta', 'urgente']).nullable(),
    nao_lidas: z.number().int(),
    /** `null` distinto de zero: ausência e "sem responsável" são a mesma coisa aqui. */
    responsavel: z
      .object({ referencia_externa: z.string(), nome: z.string().nullable() })
      .nullable(),
    /**
     * As etiquetas da conversa, pelo título — a cor vem do catálogo.
     *
     * Estão na LISTA, e não só dentro da conversa aberta, porque é aqui que
     * elas pagam o próprio custo: etiqueta serve para bater o olho numa fila
     * de trinta linhas e achar. Escondida atrás de um clique, quase não se usa.
     */
    etiquetas: z.array(z.string()),
    aberta_em: InstanteSchema,
    ultima_mensagem: z
      .object({
        trecho: z.string().nullable(),
        em: InstanteSchema,
        direcao: z.enum(['entrada', 'saida', 'sistema']),
        /**
         * O que a mensagem ERA, quando nao era texto.
         *
         * `image`, `audio`, `video`, `file` — o vocabulario do motor, o mesmo
         * de `Anexo.tipoArquivo`. Existe porque midia chega com `trecho` nulo:
         * sem este campo, uma conversa que acabou de receber um audio fica
         * igual a uma conversa que nunca recebeu nada.
         *
         * A tela decide como dizer. Guardar aqui a frase pronta faria a API
         * escrever texto de interface, e o dia de mudar a palavra viraria uma
         * mudanca de contrato.
         */
        tipo: z.string().nullable(),
      })
      .nullable(),
  }),

  caixa: z.object({
    /** Id do inbox na engine — é por ele que o filtro e o escopo (B16) trabalham. */
    referencia_externa: z.string(),
    nome: z.string().nullable(),
    /** Canal fino da matriz da A03 (C03) — nunca o `Channel::*` cru. */
    canal: z.string().nullable(),
  }),

  cliente: z
    .object({
      id: IdSchema,
      nome_exibicao: z.string().nullable(),
      /**
       * Telefone ou e-mail PARCIALMENTE MASCARADO — o campo que desempata
       * homônimo na lista (A07 E10), sem expor o contato inteiro numa tela
       * que mostra dezenas de pessoas de uma vez.
       */
      identificador_exibicao: z.string().nullable(),
    })
    .nullable(),

  vinculo: z.object({
    estado: z.enum(['vinculado', 'em_revisao', 'sem_vinculo']),
  }),

  /** Presente só quando há oportunidade vinculada — ver o cabeçalho. */
  resumo_comercial: z
    .object({
      oportunidade_id: IdSchema,
      titulo: z.string().nullable(),
      etapa: z.string().nullable(),
      /** O nome que se lê ("Novo · Agendamento"), pronto; sem ele, a tela mostra a `etapa`. */
      etapa_nome: z.string().nullable().optional(),
      valor: z.object({ quantia_micros: z.number(), moeda: z.string() }).nullable(),
      /** `true` = a projeção do Twenty ainda não chegou (D01) — só o vínculo é garantido. */
      stale: z.boolean(),
    })
    .nullable(),
});

export type ItemDaInbox = z.infer<typeof ItemDaInboxSchema>;

export const PaginaDaInboxSchema = z.object({
  itens: z.array(ItemDaInboxSchema),
  pagina: z.object({
    cursor_proximo: z.string().nullable(),
    tem_mais: z.boolean(),
    /**
     * O total DENTRO DO ESCOPO (B16) — nunca o total da tabela. Contar o
     * que o usuário não pode ver vaza a existência disso: "47 conversas"
     * quando ele alcança 12 é informação que ele não deveria ter.
     */
    total_no_escopo: z.number().int(),
  }),
  /**
   * Quantas conversas cada aba tem, com os demais filtros já aplicados.
   *
   * ── Por que vem do servidor, e não some do cliente ───────────────────────
   *
   * "Minhas" não é contável no navegador: a tela só conhece o id interno do
   * usuário, e a projeção guarda o id dele no motor de conversa — dois espaços
   * de identificador diferentes. Quem tem a ponte é o servidor.
   *
   * ── Por que com os outros filtros aplicados ─────────────────────────────
   *
   * Porque o número fica ao lado da lista. Contar sem a busca faria "Minhas 18"
   * aparecer sobre uma lista de três — e o usuário acreditaria no número, que
   * é maior e mais fácil de ler que contar linhas.
   *
   * `todas` é o universo SEM aba: com uma aba escolhida ele difere do
   * `total_no_escopo` acima, e é assim que tem de ser. Mostrar o total da aba
   * ao lado da palavra "Todos" diria que o universo encolheu porque alguém
   * clicou em "Minhas".
   */
  abas: z.object({
    minhas: z.number().int(),
    nao_atribuidas: z.number().int(),
    todas: z.number().int(),
  }),
  frescor: z.object({
    /** §14.3: a lista inteira é marcada pelo item MAIS defasado da página. */
    stale: z.boolean(),
    projetado_em: InstanteSchema.nullable(),
  }),
});

export type PaginaDaInbox = z.infer<typeof PaginaDaInboxSchema>;

/**
 * O que o sino mostra: mensagens esperando resposta.
 *
 * `mensagens` é o número dentro do ícone. `conversas` é o que a dica diz — "15
 * mensagens em 5 conversas" —, porque 15 sozinho não distingue uma pessoa
 * insistindo de cinco esperando, e são duas urgências diferentes.
 */
export const NaoLidasDaInboxSchema = z.object({
  mensagens: z.number().int().nonnegative(),
  conversas: z.number().int().nonnegative(),
});

export type NaoLidasDaInbox = z.infer<typeof NaoLidasDaInboxSchema>;

/**
 * As caixas de entrada que a pessoa atende — as opções do filtro "Caixas" da
 * lista de conversas. Só as do escopo dela (B16): oferecer uma caixa que ela
 * não alcança listaria um filtro que sempre volta vazio.
 */
export const CaixasDoAtendimentoSchema = z.object({
  itens: z.array(
    z.object({
      /** Id da caixa no atendimento: é o valor do parâmetro `caixa` da lista. */
      referencia_externa: z.string(),
      nome: z.string().nullable(),
      /** Canal fino da matriz da A03 (C03) — nunca o `Channel::*` cru. */
      canal: z.string().nullable(),
    }),
  ),
});

export type CaixasDoAtendimento = z.infer<typeof CaixasDoAtendimentoSchema>;
